import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Mail, Plus, X, Copy, Send, Phone, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthContext';
import { sendInvitationEmail } from '@/services/emailService';
import { encryptCandidateDetails } from '@/utils/encryption';
import { normalizePhoneNumber } from '@/utils/phoneUtils';
import { Alert, AlertDescription } from "@/components/ui/alert";

// Regular expressions for input validation
const URL_PATTERN = /https?:\/\/\S+/i;
const HTML_PATTERN = /<[^>]*>/g;

interface InviteCandidatesDialogProps {
  // Support both naming conventions for backward compatibility
  open?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
  onCopyLink?: () => void;
  assessmentId?: string;
  assessmentTitle: string;
}

interface Candidate {
  name: string;
  email: string;
  phone: string;
}

interface Organization {
  name: string;
}

interface AssessmentData {
  candidates_invited: number;
  organization_id: string;
  organizations: Organization;
}

const InviteCandidatesDialog: React.FC<InviteCandidatesDialogProps> = ({
  open,
  isOpen,
  onOpenChange,
  onClose,
  onCopyLink,
  assessmentId,
  assessmentTitle,
}) => {
  const [candidates, setCandidates] = useState<Candidate[]>([{ name: '', email: '', phone: '' }]);
  const [isSending, setIsSending] = useState(false);
  const [sendEmails, setSendEmails] = useState(true);
  const [makeCalls, setMakeCalls] = useState(false);
  const { user, profile } = useAuth();
  
  // Check if any candidates have phone numbers
  const hasPhoneNumbers = candidates.some(candidate => candidate.phone.trim() !== '');
  
  // Auto-disable makeCalls when no phone numbers are present
  React.useEffect(() => {
    if (!hasPhoneNumbers && makeCalls) {
      setMakeCalls(false);
    }
  }, [hasPhoneNumbers, makeCalls]);
  
  // Use either open or isOpen prop (isOpen takes precedence)
  const isDialogOpen = isOpen !== undefined ? isOpen : open;

  // Handle dialog state changes
  const handleOpenChange = (newOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(newOpen);
    } else if (onClose && !newOpen) {
      onClose();
    }
  };

  // Handle copy link button click
  const handleCopyLink = async () => {
    if (onCopyLink) {
      onCopyLink();
    } else {
      // Default behavior if onCopyLink is not provided
      if (!assessmentId) {
        toast.error('Assessment ID is required');
        return;
      }

      const registrationToken = encryptCandidateDetails({
        type: 'registration',
        assessmentId,
      });
      
      // Create the original long URL
      const assessmentLink = `${window.location.origin}/assessment/${assessmentId}/landing?token=${registrationToken}`;
      
      try {
        // Try to shorten the URL first
        const { shortenUrl } = await import('@/services/url-shortener');
        
        // Set expiration to 30 days (in seconds)
        const expiresIn = 30 * 24 * 60 * 60;
        
        // Try to shorten the URL
        const shortUrl = await shortenUrl(assessmentLink, expiresIn);
        
        // Copy the shortened URL to clipboard
        await navigator.clipboard.writeText(shortUrl);
        toast.success('Shortened assessment link copied to clipboard');
      } catch (shortenError) {
        console.error('Failed to shorten link:', shortenError);
        
        // Fall back to the original long URL if shortening fails
        navigator.clipboard.writeText(assessmentLink)
          .then(() => {
            toast.success('Assessment link copied to clipboard');
          })
          .catch((error) => {
            console.error('Failed to copy link:', error);
            toast.error('Failed to copy link to clipboard');
          });
      }
    }
  };

  const handleAddCandidate = () => {
    setCandidates([...candidates, { name: '', email: '', phone: '' }]);
  };

  const handleRemoveCandidate = (index: number) => {
    const newCandidates = [...candidates];
    newCandidates.splice(index, 1);
    if (newCandidates.length === 0) {
      newCandidates.push({ name: '', email: '', phone: '' });
    }
    setCandidates(newCandidates);
  };

  const handleCandidateChange = (index: number, field: keyof Candidate, value: string) => {
    // Sanitize input by removing HTML tags
    let sanitizedValue = value;
    
    if (field === 'name') {
      // Remove HTML tags
      sanitizedValue = value.replace(HTML_PATTERN, '');
      
      // Check for URLs in name field
      if (URL_PATTERN.test(sanitizedValue)) {
        toast.error("URLs are not allowed in the name field");
        return;
      }
    } else if (field === 'phone') {
      // Normalize phone number format
      sanitizedValue = normalizePhoneNumber(value);
    }
    
    const newCandidates = [...candidates];
    newCandidates[index][field] = sanitizedValue;
    setCandidates(newCandidates);
  };

  const handleInviteCandidates = async () => {
    if (!assessmentId) {
      toast.error("Assessment ID is required");
      return;
    }

    if (!user) {
      toast.error("You must be logged in to invite candidates");
      return;
    }

    // Additional validation before submission
    for (const candidate of candidates) {
      // Check for HTML tags in candidate name
      if (HTML_PATTERN.test(candidate.name)) {
        toast.error("HTML tags are not allowed in candidate names");
        return;
      }
      
      // Check for URLs in candidate name
      if (URL_PATTERN.test(candidate.name)) {
        toast.error("URLs are not allowed in candidate names");
        return;
      }
    }

    // Validate based on selected invitation methods
    if (!sendEmails && !makeCalls) {
      toast.error("Please select at least one invitation method");
      return;
    }

    let validCandidates = candidates.filter(candidate => candidate.name.trim() !== '');
    
    if (sendEmails && !makeCalls) {
      // Email only: require email
      validCandidates = validCandidates.filter(candidate => 
        candidate.email.trim() !== '' && candidate.email.includes('@')
      );
      if (validCandidates.length === 0) {
        toast.error("Please enter at least one valid candidate with name and email");
        return;
      }
    } else if (makeCalls && !sendEmails) {
      // Calls only: require phone
      validCandidates = validCandidates.filter(candidate => 
        candidate.phone.trim() !== ''
      );
      if (validCandidates.length === 0) {
        toast.error("Please enter at least one valid candidate with name and phone number");
        return;
      }
    } else {
      // Both: require at least email OR phone
      validCandidates = validCandidates.filter(candidate => 
        (candidate.email.trim() !== '' && candidate.email.includes('@')) ||
        candidate.phone.trim() !== ''
      );
      if (validCandidates.length === 0) {
        toast.error("Please enter at least one valid candidate with name and either email or phone");
        return;
      }
    }
    
    setIsSending(true);
    console.log('Starting invitation process for', validCandidates.length, 'candidates');
    
    try {
      // Get sender info from user profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', user.id)
        .single();
      
      // Create sender name
      let senderName = 'RoundOne Team';
      if (profileData) {
        const firstName = profileData.first_name || '';
        const lastName = profileData.last_name || '';
        if (firstName || lastName) {
          senderName = `${firstName} ${lastName}`.trim();
        }
      }
      
      // Step 1: Check for existing candidates with the same emails
      console.log('Checking for existing candidates...');
      const emails = validCandidates.map(candidate => candidate.email.trim());
      
      const { data: existingCandidates, error: existingError } = await supabase
        .from('candidates')
        .select('id, email, phone')
        .eq('assessment_id', assessmentId)
        .in('email', emails);
        
      if (existingError) {
        console.error('Error checking existing candidates:', existingError);
        throw new Error(`Failed to check existing candidates: ${existingError.message}`);
      }
      
      // Create sets for easy lookup
      const existingEmails = new Set(existingCandidates?.map(c => c.email.toLowerCase()) || []);
      console.log('Existing candidate emails:', existingEmails);
      
      // Filter out candidates that already exist
      const newCandidates = validCandidates.filter(
        candidate => !existingEmails.has(candidate.email.trim().toLowerCase())
      );
      
      const duplicateCandidates = validCandidates.filter(
        candidate => existingEmails.has(candidate.email.trim().toLowerCase())
      );
      
      console.log('New candidates to invite:', newCandidates);
      console.log('Duplicate candidates:', duplicateCandidates);
      
      let allCandidateIds: string[] = [];
      let insertedCandidates: any[] = [];
      
      // Step 2: Insert new candidates if any
      if (newCandidates.length > 0) {
        console.log('Inserting new candidates into database:', newCandidates);
        const { data: insertResult, error: insertError } = await supabase
          .from('candidates')
          .insert(newCandidates.map(candidate => ({
            assessment_id: assessmentId,
            name: candidate.name.trim(),
            email: candidate.email.trim(),
            phone: candidate.phone.trim() || null,
            status: 'invited'
          })))
          .select('id, name, email, phone');

        if (insertError) {
          console.error('Error inserting candidates:', insertError);
          throw new Error(`Failed to insert candidates: ${insertError.message}`);
        }
        
        insertedCandidates = insertResult || [];
        allCandidateIds.push(...insertedCandidates.map(c => c.id));
        console.log('Successfully inserted candidates:', insertedCandidates);
      }
      
      // Add existing candidate IDs
      if (existingCandidates && existingCandidates.length > 0) {
        allCandidateIds.push(...existingCandidates.map(c => c.id));
      }
      
      // Step 3: Update assessment count for new candidates only
      if (newCandidates.length > 0) {
        const { data: assessmentData, error: fetchError } = await supabase
          .from('assessments')
          .select(`
            candidates_invited,
            organization_id,
            organizations (
              name
            )
          `)
          .eq('id', assessmentId)
          .single<AssessmentData>();

        if (fetchError) {
          console.error('Error fetching assessment data:', fetchError);
        } else {
          const newCount = (assessmentData?.candidates_invited || 0) + newCandidates.length;
          const { error: updateError } = await supabase
            .from('assessments')
            .update({ candidates_invited: newCount })
            .eq('id', assessmentId);
            
          if (updateError) {
            console.error('Error updating assessment count:', updateError);
          } else {
            console.log('Updated assessment count to:', newCount);
          }
          
          // Step 4: Send invitation emails to new candidates (if email option is selected)
          if (sendEmails && insertedCandidates.length > 0) {
            console.log('Sending batch emails to new candidates');
            const candidatesWithEmail = insertedCandidates.filter(candidate => 
              candidate.email && candidate.email.includes('@')
            );
            
            if (candidatesWithEmail.length > 0) {
              const candidatesForEmail = candidatesWithEmail.map(candidate => {
                const token = encryptCandidateDetails({
                  type: 'invite',
                  assessmentId: assessmentId,
                  candidateId: candidate.id,
                  name: candidate.name,
                  email: candidate.email
                });
                
                return {
                  recipientEmail: candidate.email,
                  recipientName: candidate.name,
                  assessmentLink: `${window.location.origin}/assessment/${assessmentId}/landing?token=${token}`,
                  companyName: assessmentData?.organizations?.name || 'RoundOne AI'
                };
              });
              
              const emailSuccess = await sendInvitationEmail(
                candidatesForEmail,
                assessmentTitle,
                senderName,
                assessmentId
              );
              console.log('Email sending result:', emailSuccess);
            }
          }
        }
      }
      
      // Step 5: Make phone calls to candidates with phone numbers (if call option is selected)
      if (makeCalls) {
        const candidatesWithPhone = [
          ...insertedCandidates.filter(c => c.phone),
          ...(existingCandidates?.filter(c => c.phone) || [])
        ];
        
        if (candidatesWithPhone.length > 0) {
          console.log('Making outbound calls to candidates with phone numbers:', candidatesWithPhone.length);
          try {
            const { data: callResult, error: callError } = await supabase.functions.invoke(
              'candidate-outbound-call',
              {
                body: {
                  candidateIds: candidatesWithPhone.map(c => c.id),
                  assessmentId
                }
              }
            );

            if (callError) {
              console.error('Error making outbound calls:', callError);
              toast.error('Failed to initiate phone calls, but email invitations were sent successfully');
            } else if (callResult?.success) {
              console.log('Call results:', callResult);
              if (callResult.estimatedDuration) {
                toast.info(`Phone calls initiated. Estimated assessment duration: ${callResult.estimatedDuration}`);
              }
            }
          } catch (callError) {
            console.error('Error making calls:', callError);
            toast.error('Failed to initiate phone calls, but email invitations were sent successfully');
          }
        }
      }
      
      // Step 6: Show summary messages
      const emailCount = sendEmails ? newCandidates.filter(c => c.email && c.email.includes('@')).length : 0;
      const callCount = makeCalls ? [...insertedCandidates.filter(c => c.phone), ...(existingCandidates?.filter(c => c.phone) || [])].length : 0;
      const duplicateCount = duplicateCandidates.length;
      
      let successMessage = '';
      if (emailCount > 0 && callCount > 0) {
        successMessage = `Successfully invited ${emailCount} candidate(s) via email and initiated calls to ${callCount} candidate(s)`;
      } else if (emailCount > 0) {
        successMessage = `Successfully invited ${emailCount} candidate(s) via email`;
      } else if (callCount > 0) {
        successMessage = `Successfully initiated calls to ${callCount} candidate(s)`;
      }
      
      if (successMessage) {
        toast.success(successMessage);
      }
      
      if (duplicateCount > 0) {
        toast.info(`${duplicateCount} candidate(s) were already invited previously`);
      }
      
      if (newCandidates.length === 0 && duplicateCount > 0) {
        toast.info('All candidates have already been invited to this assessment');
      }
        
      setIsSending(false);
      handleOpenChange(false);
      
    } catch (error: any) {
      console.error('Error in invitation process:', error);
      toast.error(error.message || 'Failed to invite candidates');
      setIsSending(false);
    }
  };



  return (
    <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" 
        style={{
          position: 'fixed',
          top: '40%',
          maxWidth: '42rem',
          width: '95%',
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          border: '1px solid rgba(155, 135, 245, 0.3)',
          zIndex: 50
        }}>
        <DialogHeader>
          <DialogTitle>Invite Candidates</DialogTitle>
          <DialogDescription>
            Assessment: {assessmentTitle}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <h3 className="text-sm font-medium mb-2">Assessment: {assessmentTitle}</h3>
          
          <div className="space-y-3 mb-6">
            {candidates.map((candidate, index) => (
              <div key={index} className="flex items-center gap-3">
                <Input
                  placeholder="Name"
                  value={candidate.name}
                  onChange={(e) => handleCandidateChange(index, 'name', e.target.value)}
                  className="w-[22%]"
                />
                <Input
                  placeholder="Email"
                  value={candidate.email}
                  onChange={(e) => handleCandidateChange(index, 'email', e.target.value)}
                  className="w-[35%]"
                />
                <PhoneInput
                  placeholder="Phone"
                  value={candidate.phone}
                  onChange={(value) => handleCandidateChange(index, 'phone', value)}
                  className="w-[43%]"
                />
                {candidates.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveCandidate(index)}
                    className="shrink-0"
                    aria-label="Remove candidate"
                  >
                    <X size={18} />
                  </Button>
                )}
              </div>
            ))}
            
            <Button
              variant="light-ghost"
              size="sm"
              onClick={handleAddCandidate}
              className="flex items-center text-[#9b87f5]"
            >
              <Plus size={16} className="mr-1" /> Add Another Candidate
            </Button>
          </div>

          {/* Invitation Method Selection */}
          <div className="space-y-4 mb-6 border-t pt-4">
            <h4 className="text-sm font-medium text-gray-700">Invitation Methods</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="send-emails"
                  checked={sendEmails}
                  onCheckedChange={(checked) => setSendEmails(checked === true)}
                />
                <Label htmlFor="send-emails" className="text-sm cursor-pointer flex items-center">
                  <Mail size={16} className="mr-2" />
                  Send Email Invitations
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="make-calls"
                  checked={makeCalls}
                  disabled={!hasPhoneNumbers}
                  onCheckedChange={(checked) => setMakeCalls(checked === true)}
                />
                <Label 
                  htmlFor="make-calls" 
                  className={`text-sm flex items-center ${
                    hasPhoneNumbers 
                      ? 'cursor-pointer text-gray-700' 
                      : 'cursor-not-allowed text-gray-400'
                  }`}
                >
                  <Phone size={16} className="mr-2" />
                  Call Candidates {!hasPhoneNumbers && '(add phone numbers to enable)'}
                  {hasPhoneNumbers && `(${candidates.filter(c => c.phone.trim() !== '').length} phone number${candidates.filter(c => c.phone.trim() !== '').length === 1 ? '' : 's'} entered)`}
                </Label>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col gap-3">
            <Button 
              variant="purple" 
              onClick={handleInviteCandidates}
              disabled={isSending}
              className="w-full"
            >
              {isSending ? (
                <>Inviting Candidates...</>
              ) : (
                <>
                  <Send size={16} className="mr-1" /> Invite Candidates
                </>
              )}
            </Button>
            
            <div className="relative flex items-center">
              <div className="flex-grow border-t border-gray-300"></div>
              <span className="mx-2 flex-shrink text-xs text-gray-500">OR</span>
              <div className="flex-grow border-t border-gray-300"></div>
            </div>
            
            <Button 
              variant="light-outline" 
              onClick={handleCopyLink}
              className="w-full"
            >
              <Copy size={16} className="mr-1" /> Copy Assessment Link
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InviteCandidatesDialog;
